/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import _slice from "lodash/slice";
import BankAccount from "./BankAccount/BankAccount";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import { requiredValidator } from "@/src/services/validationRules";
import { Box, MenuItem } from "@mui/material";
import _cloneDeep from "lodash/cloneDeep";
import { useForm } from "@/src/provider/Form/FormProvider";
import _toNumber from "lodash/toNumber";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import wizardStyle from "@/src/components/Form/wizardStyle";

type BankAccountProps = {
  submitProps: FormSubmitProps;
};

export default function BankAccounts({ submitProps }: BankAccountProps) {
  const { values, handleChange, handleSubmit } = useForm();

  useEffect(() => {
    const numberOfBankAccounts = values.numberOfBankAccounts ? _toNumber(values.numberOfBankAccounts) : 0;
    if (values.bankAccounts.length === numberOfBankAccounts) {
      return;
    }
    const emptyAccount = {
      type: "",
      owner: "",
      name: "",
      amount: "",
    };
    let bankAccounts = _cloneDeep(values.bankAccounts);
    for (let i = bankAccounts.length; i < Math.min(numberOfBankAccounts, 5); i++) {
      bankAccounts.push(_cloneDeep(emptyAccount));
    }
    bankAccounts = _slice(bankAccounts, 0, numberOfBankAccounts);
    handleChange({
      target: {
        type: "other",
        name: "bankAccounts",
        value: bankAccounts,
      },
    });
  }, [values.bankAccounts, values.numberOfBankAccounts]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Vermögen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                Bitte geben Sie hier Ihr Bank-, Giro- oder Sparkonto an (auch Gemeinschaftskonten). Die Angaben zu Ihrem Konto sind Pflicht für die Bewilligung
                von Verfahrenskostenhilfe. Auch wenn Ihr Guthaben bei Null steht oder im Minus ist, geben Sie das Konto bitte an.
              </FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorSelectField
                label="Wie viele Bank-, Giro- oder Sparkonto haben Sie?"
                name="numberOfBankAccounts"
                validators={[requiredValidator("Bitte geben Sie an wie viele Bank-, Giro- oder Sparkonto Sie haben")]}
              >
                <MenuItem value={"1"}>1</MenuItem>
                <MenuItem value={"2"}>2</MenuItem>
                <MenuItem value={"3"}>3</MenuItem>
                <MenuItem value={"4"}>4</MenuItem>
                <MenuItem value={"5"}>5</MenuItem>
              </ValidatorSelectField>
            </Grid>
            {values.bankAccounts?.map((bankAccount: any, index: number) => (
              <BankAccount key={index} index={index} path={`bankAccounts[${index}]`} />
            ))}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
